import { useEffect, useState } from 'preact/hooks';
import type { PhoneApp } from '../app/phone.js';
import type { LineView } from '../sip/lines.js';
import type { HeldBridge, PhoneState } from '../relay/reducer.js';
import { formatClock } from '../keepalive/timers.js';
import { formatAuNumber } from './format.js';

/** re-render every second for countdowns and call timers */
export function useTick(): number {
  const [t, setT] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setT((x) => x + 1), 1000);
    return () => clearInterval(h);
  }, []);
  return t;
}

export function RingCard({ app, line }: { app: PhoneApp; line: LineView }) {
  useTick();
  const call = line.callId ? app.store.state.value.calls[line.callId] : undefined;
  const remaining = line.ringDeadline ? Math.max(0, line.ringDeadline - app.clock.now()) : null;
  const crm = call?.crm_match as { title?: string; url?: string } | undefined;
  return (
    <section class={`card ring${line.callWaiting ? ' compact' : ''}`} role="alert" aria-label="Incoming call" data-testid="ring-card" data-line={line.id} data-call-id={line.callId ?? ''}>
      <div style={{ flex: 1 }}>
        <div class="who">{line.remoteName || formatAuNumber(line.remoteNumber) || 'Unknown caller'}</div>
        <div class="sub">
          {line.remoteName ? formatAuNumber(line.remoteNumber) : ''}
          {call?.queue_name ? ` · ${call.queue_name}` : ''}
          {line.callWaiting ? ' · call waiting' : ''}
          {remaining !== null ? ` · ${formatClock(remaining)}` : ''}
        </div>
        {crm?.title ? (
          <div>
            {crm.url ? (
              <a class="chip" href={crm.url} target="_blank" rel="noreferrer noopener">
                {crm.title}
              </a>
            ) : (
              <span class="chip">{crm.title}</span>
            )}
          </div>
        ) : null}
      </div>
      <div class="row">
        <button class="primary" data-testid="ring-answer" disabled={line.declining} onClick={() => void app.answer(line.id)} aria-keyshortcuts="Enter">
          Answer <span class="kbd">Enter</span>
        </button>
        <button class="danger" data-testid="ring-decline" disabled={line.declining} onClick={() => void app.decline(line.id, false)} aria-keyshortcuts="Escape">
          Decline <span class="kbd">Esc</span>
        </button>
        <button data-testid="ring-voicemail" disabled={line.declining} onClick={() => void app.decline(line.id, true)}>
          Send to voicemail
        </button>
        {line.declining ? <span class="spinner" aria-label="Declining" /> : null}
      </div>
    </section>
  );
}

export function RejoinCard({ app, bridge }: { app: PhoneApp; bridge: HeldBridge }) {
  useTick();
  const left = app.clock.until(bridge.expires_at);
  return (
    <section class="card rejoin" aria-label="Rejoin call">
      <div class="who">Call on hold for you · {formatClock(left)} left</div>
      <ul class="roster">
        {bridge.roster.map((r) => (
          <li key={r.channel_id}>
            {r.label} {r.number ? <span class="sub">{formatAuNumber(r.number)}</span> : null}
          </li>
        ))}
      </ul>
      <div class="row">
        <button class="primary" onClick={() => void app.rejoin(bridge.call_id, bridge.bridge_id)}>
          Rejoin
        </button>
      </div>
    </section>
  );
}

export function Toasts({ app }: { app: PhoneApp }) {
  const toasts = app.toasts.value;
  if (toasts.length === 0) return null;
  return (
    <div class="toasts" aria-live="assertive">
      {toasts.map((t) => (
        <div key={t.id} class={`toast ${t.kind}`} data-testid="toast" data-kind={t.kind}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function SoundStrip({ app }: { app: PhoneApp }) {
  if (!app.soundNeedsClick.value) return null;
  return (
    <button class="strip" onClick={() => void app.enableSound()} aria-label="Click to enable sound">
      Click to enable sound
    </button>
  );
}

export function MicDeniedCard({ app }: { app: PhoneApp }) {
  if (!app.micDenied.value) return null;
  return (
    <section class="card lost" role="alert">
      <div class="who">Microphone not available</div>
      <div class="sub">
        Click the camera/microphone icon in the address bar, allow the microphone for this site, then press Retry. Inbound calls still ring; your queues are paused until the
        microphone works.
      </div>
      <div class="row">
        <button class="primary" onClick={() => void app.probeMic()}>
          Retry
        </button>
      </div>
    </section>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

export function Banners({ app }: { app: PhoneApp }) {
  useTick();
  const s: PhoneState = app.store.state.value;
  const out: preact.JSX.Element[] = [];
  const sip = app.sipState.value;
  const relay = app.relayStatus.value;

  if (s.nodeState === 'down') {
    out.push(
      <div class="banner red" role="alert" key="down">
        Phone server unavailable{s.nodeIncidentId ? ` — incident ${s.nodeIncidentId}` : ''}
        {s.nodeMessage ? ` · ${s.nodeMessage}` : ''}
        <span class="spacer" />
        <button onClick={() => app.retryNow()}>Retry now</button>
      </div>,
    );
  } else if (s.nodeState === 'restarting' || s.nodeState === 'draining') {
    const eta = s.nodeEtaSec !== undefined && s.nodeStateAt ? Math.max(0, s.nodeEtaSec * 1000 - (app.clock.now() - Date.parse(s.nodeStateAt))) : null;
    out.push(
      <div class="banner amber" role="status" key="restarting">
        Phone server restarting{eta !== null ? ` — back in about ${formatClock(eta)}` : ''}
      </div>,
    );
  } else if (s.maintenance.active) {
    const restartIn = s.maintenance.restart_at ? app.clock.until(s.maintenance.restart_at) : null;
    const soon = restartIn !== null && restartIn <= 2 * 60_000;
    out.push(
      <div class={`banner ${soon ? 'amber' : 'grey'}`} role="status" key="maint">
        {soon
          ? 'Please finish your call — phone server restart imminent'
          : `Phone server restart${s.maintenance.restart_at ? ` at ${fmtTime(s.maintenance.restart_at)} (in ${formatClock(restartIn ?? 0)})` : ' scheduled'}`}
        {s.pausedQueues.length > 0 ? ' · your queues are paused' : ''}
        {s.maintenance.message ? ` · ${s.maintenance.message}` : ''}
      </div>,
    );
  }
  if (s.nodeState === 'up' && (sip === 'connecting' || sip === 'reconnecting') && relay === 'open') {
    out.push(
      <div class="banner grey" role="status" key="reconnecting">
        {sip === 'connecting' ? 'Reconnected — restoring your extension' : 'Reconnecting to the phone server…'}
      </div>,
    );
  }
  if (relay !== 'open' && relay !== 'connecting' && relay !== 'idle') {
    out.push(
      <div class="banner grey" role="status" key="relay">
        {app.lines.value.some((l) => l.state === 'up') ? 'Signalling lost — audio continues' : 'Control connection lost — reconnecting'}
      </div>,
    );
  }
  if (sip === 'failed') {
    out.push(
      <div class="banner red" role="alert" key="failed">
        Secure connection failed — check the computer's date and time
      </div>,
    );
  }
  if (s.control === 'reconnecting' || s.control === 'down') {
    out.push(
      <div class="banner grey" role="status" key="control">
        Call controller {s.control === 'down' ? 'down' : 'reconnecting'} — calls continue, controls may be delayed
      </div>,
    );
  }
  if (s.mwi.waiting) {
    out.push(
      <div class="banner green" role="status" key="mwi">
        Voicemail: {s.mwi.new} new{s.mwi.old ? `, ${s.mwi.old} old` : ''}
      </div>,
    );
  }
  return <>{out}</>;
}

export function SupersededCard({ app }: { app: PhoneApp }) {
  const at = app.supersededAt.value;
  return (
    <section class="card lost" role="alert" data-testid="superseded">
      <div class="who">Signed in on another device{at ? ` at ${fmtTime(at)}` : ''}</div>
      <div class="sub">This window has stopped registering. Use the other device, or take the phone back here.</div>
      <div class="row">
        <button class="primary" onClick={() => void app.useThisDevice()}>
          Use this device
        </button>
      </div>
    </section>
  );
}

export function RelaunchCard({ reason }: { reason: string | null }) {
  const text: Record<string, string> = {
    logout: 'You signed out.',
    expired: 'Your phone session expired.',
    refresh_rejected: 'Your phone session could not be renewed.',
    register_rejected: 'The phone server rejected this session.',
    register_retry_exhausted: 'The phone server kept rejecting this session for 10 minutes.',
    zombie: 'This session was closed after a long silence.',
    admin: 'An administrator ended this session.',
    superseded: 'Another device holds the phone now.',
    no_session: 'No phone session on this device.',
    forbidden: 'Could not sign the phone in.',
    maintenance: 'Sign-in is paused for maintenance.',
    not_found: 'This launch link has expired or was already used.',
    network: 'Could not reach the phone service.',
    invalid: 'The phone service answered with something unexpected.',
  };
  return (
    <section class="card" role="alert" data-testid="relaunch" data-reason={reason ?? ''}>
      <div class="who">{text[reason ?? ''] ?? 'The phone needs to be opened again.'}</div>
      <div class="sub">Open the phone again from your CRM (the phone dock's Open phone button).</div>
    </section>
  );
}

export function InstallButton({ app }: { app: PhoneApp }) {
  const inst = app.install;
  if (inst.installed.value) return null;
  if (inst.available.value) {
    return (
      <button class={inst.nag.value ? 'primary' : ''} onClick={() => void inst.prompt()} title="Install the phone as an app; then enable run on login via chrome://apps or the policy pack">
        Install phone
      </button>
    );
  }
  return null;
}
